import React, { forwardRef } from 'react';

interface FormProps {
  onSubmit?: React.FormEventHandler<HTMLFormElement>;
  className?: string;
  children?: React.ReactNode;
  'aria-label'?: string;
}

function FormBase({ onSubmit, className = '', children, ...rest }: FormProps) {
  return (
    <form onSubmit={onSubmit} className={className} {...rest}>
      {children}
    </form>
  );
}

interface GroupProps {
  className?: string;
  children?: React.ReactNode;
  'aria-label'?: string;
}

function FormGroup({ className = '', children, ...rest }: GroupProps) {
  return <div className={`mb-4 ${className}`.trim()} {...rest}>{children}</div>;
}

interface ControlProps extends React.InputHTMLAttributes<HTMLInputElement> {
  as?: 'input' | 'textarea';
}

const FormControl = forwardRef<HTMLInputElement, ControlProps>(
  function FormControl({ as, className = '', ...rest }, ref) {
    return <input ref={ref} className={`input input-bordered w-full ${className}`.trim()} {...rest} />;
  }
);

interface CheckProps {
  type?: string;
  id?: string;
  label?: string;
  checked?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  disabled?: boolean;
  'aria-describedby'?: string;
  className?: string;
}

function FormCheck({ type = 'checkbox', id, label, checked, onChange, disabled, className = '', ...rest }: CheckProps) {
  return (
    <div className={`flex items-center gap-2 ${className}`.trim()}>
      <input
        className="checkbox"
        type={type}
        id={id}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        {...rest}
      />
      {label && <label className="label cursor-pointer" htmlFor={id}>{label}</label>}
    </div>
  );
}

interface LabelProps {
  className?: string;
  children?: React.ReactNode;
  'aria-label'?: string;
  htmlFor?: string;
}

function FormLabel({ className = '', children, ...rest }: LabelProps) {
  return <label className={`label-text font-medium ${className}`.trim()} {...rest}>{children}</label>;
}

interface TextProps {
  id?: string;
  muted?: boolean;
  className?: string;
  children?: React.ReactNode;
}

function FormText({ id, muted, className = '', children }: TextProps) {
  const mutedClass = muted ? 'text-base-content/60' : '';
  return <small id={id} className={`text-xs ${mutedClass} ${className}`.trim()}>{children}</small>;
}

interface RangeProps extends React.InputHTMLAttributes<HTMLInputElement> {}

function FormRange({ className = '', ...rest }: RangeProps) {
  return <input type="range" className={`range ${className}`.trim()} {...rest} />;
}

const Form = Object.assign(FormBase, {
  Group: FormGroup,
  Control: FormControl,
  Check: FormCheck,
  Label: FormLabel,
  Text: FormText,
  Range: FormRange,
});

export default Form;
